import { useState, useEffect, useRef } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useLazyQuery } from "@apollo/client";


import { TextBadge } from "../common";
import GenericSelectableTable from "../selectableTable";
import useApi from "../../api";
import { NXPrint, NXEyes } from "../../icons";
import enumChoice, { getEnumLabel } from "../../enum";
import Invoice from "./Invoice";
import OrderDetails from "../orders/orderDetails";
import { generateBulkInvoice, GET_INVOICES } from "../../pdf/generateBulkInvoice";
import { generateBulkReceiptPOS } from "../../pdf/generateBulkReceiptPOS";

import InvoiceChooser from "./invoiceChooser";

function OrderTable({ orders }: any) {
  const api = useApi();
  const [invoice, setInvoice] = useState<any>({});
  const [openOrderDetails, setOpenOrderDetails] = useState<any>(null);
  const [openInvoiceChooser, setOpenInvoiceChooser] = useState<boolean>(false);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [printInvoice, setPrintInvoice] = useState<any>(null);
  const [printReceipt, setPrintReceipt] = useState<any>(null);
  
  const [fetchInvoices, { data, loading, error }] = useLazyQuery(GET_INVOICES);

 

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
  if (printRef.current) {
    const printContent = printRef.current.innerHTML;
    const printWindow = document.createElement('iframe');
    
    printWindow.style.position = 'absolute';
    printWindow.style.width = '0';
    printWindow.style.height = '0';
    printWindow.style.border = 'none';

    document.body.appendChild(printWindow); // Attach the iframe to the document

    const doc = printWindow.contentDocument || printWindow.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(`
        <html>
        <head>
          <title>Invoice Print</title>
          ${Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map(style => style.outerHTML)
            .join('')}
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);
      doc.close();

      printWindow.contentWindow?.focus();
      printWindow.contentWindow?.print();
      
      // Clean up the iframe after printing to allow repeated prints
      printWindow.contentWindow?.addEventListener('afterprint', () => {
        document.body.removeChild(printWindow);
      });
    }
  }
};


const handleClick = (e: any, alias: any) => {
  api.getInvoice(alias)
    .then((response) => {
      setInvoice(response);
    })
    .catch(() => toast.error("Error fetching invoice"));
};

useEffect(() => {
  if (Object.keys(invoice).length > 0) {
    handlePrint();
  }
}, [invoice]);


useEffect(() => {
  if (data) {
    if (printInvoice) {
      generateBulkInvoice(data);
      setPrintInvoice(false);
    }

    if (printReceipt) {
      generateBulkReceiptPOS(data);
      setPrintReceipt(false);
    }

  }
}, [data]);

useEffect(() => {
  if (error) {
    toast.error(`Error fetching invoices: ${error.message}`);
  }
}, [error]);


const bullkActionButtons = [
  {
    label: "Invoices",
    icon: <NXPrint className="w-5 h-5" />,
    onClick: (selectedItems: any) => {
      fetchInvoices({ variables: { orderIds: Array.from(selectedItems) } });
      setPrintInvoice(true);
    }
  },
  {
    label: "Invoices As",
    icon: <NXPrint className="w-5 h-5" />,
    onClick: (selectedItems: any) => {
      setSelectedOrders(Array.from(selectedItems));
      setOpenInvoiceChooser(true);
    }
  },
  {
    label: "Receipts",
    icon: <NXPrint className="w-5 h-5" />,
    onClick: (selectedItems: any) => {
      fetchInvoices({ variables: { orderIds: Array.from(selectedItems) } });
      setPrintReceipt(true);
    }
  },
];

// Define columns with render functions for custom content
const columns = [
  { header: "Order", key: "node.dbId", render: (item: any) => (
    <a onClick={() => setOpenOrderDetails(item.node.alias)} className="cursor-pointer">
      <p>{item.node.dbId}</p>
      <p className="text-sm font-normal text-base-300 mt-1">#ID{item.node.dbId}</p>
    </a>
  )},
  { header: "Date and Time", key: "created_at", render: (item: any) => (
    moment(item.node.createdAt).format("MMMM D, YYYY h:mm A")
  )},
  // { header: "Customer", key: "user" },
  { header: "Payment Method", key: "payment_method", render: (item: any) => (
    <TextBadge value={item.node.paymentMethod} label={getEnumLabel(enumChoice.paymentMethods, item.paymentMethod)} />
  )},
  { header: "Status", key: "node.status", render: (item: any) => (
    <TextBadge value={item.node.status} label={getEnumLabel(enumChoice.orderStatus, item.node.status)} />
  )},
  { header: "Price", key: "node.humanizeTotalPrice" },
  { header: "Actions", key: "actions", render: (item: any) => (
    <div className="flex gap-2">
      <button className="text-primary-500 hover:underline" onClick={() => setOpenOrderDetails(item.node.alias)}>
        <NXEyes className="hover:stroke-primary-400 cursor-pointer transition-all duration-150 ease-linear" />
      </button>
      <button className="text-primary-500 hover:underline" onClick={(e) => handleClick(e, item.node.alias)}>
        <NXPrint className="hover:stroke-primary-400 cursor-pointer transition-all duration-150 ease-linear"/>
      </button>
    </div>
  )}
];

const printableColumn = [
  { header: "order", key: "node.dbId" },
  { header: "date", key: "node.createdAt" },
  { header: "payment method", key: "paymentMethod" },
  { header: "status", key: "node.status" },
  { header: "price", key: "node.humanizeTotalPrice" },

]



return (
  <div className="relative overflow-x-auto">
    {/* Hidden element for printing */}
    <div ref={printRef} style={{ display: "none" }}>
      <Invoice data={invoice} />
    </div>

    <GenericSelectableTable bullkActionButtons={bullkActionButtons}  printableColumn={printableColumn} columns={columns} data={orders} />

    {/* Order details modal */}
    {openOrderDetails && (
      <OrderDetails isOpen={openOrderDetails ? true : false} onClose={() => setOpenOrderDetails(null)} alias={openOrderDetails} />
    )}


    { openInvoiceChooser && <InvoiceChooser selectedItems={selectedOrders} isOpen={openInvoiceChooser} onClose={() =>  setOpenInvoiceChooser(false) } />}

  </div>
);
}

export default OrderTable;
